import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Photo lightbox with optional gallery navigation.
// items: [{ src, caption }]. index: current index. onClose, onPrev, onNext.
export const Lightbox = ({ items = [], index = 0, onClose, onIndexChange }) => {
  const open = index !== null && index !== undefined && index >= 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onIndexChange?.((index - 1 + items.length) % items.length);
      if (e.key === "ArrowRight") onIndexChange?.((index + 1) % items.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, index, items.length, onClose, onIndexChange]);

  const current = open ? items[index] : null;

  return (
    <AnimatePresence>
      {open && current && (
        <motion.div
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          onClick={onClose}
        >
          <button
            data-testid="lightbox-close"
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            className="absolute top-5 right-5 md:top-8 md:right-10 text-white/80 hover:text-white transition-colors z-10"
            aria-label="Close"
          >
            <X size={28} />
          </button>

          {items.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); onIndexChange?.((index - 1 + items.length) % items.length); }}
                className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10"
                aria-label="Previous"
              >
                <ChevronLeft size={40} />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); onIndexChange?.((index + 1) % items.length); }}
                className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10"
                aria-label="Next"
              >
                <ChevronRight size={40} />
              </button>
            </>
          )}

          <motion.div
            key={current.src}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-6xl w-full flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={current.src}
              alt={current.caption || ""}
              className="w-full max-h-[80vh] object-contain"
            />
            {current.caption && (
              <p className="font-serif italic text-white/90 text-center text-base md:text-lg max-w-3xl px-4">
                {current.caption}
              </p>
            )}
            {items.length > 1 && (
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </span>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
